import { IconButton, Tooltip } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@/stores/authStore';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useNotify } from '@/hooks/useNotify';
import {
  useHasWorkOrderWindow,
  useWorkOrderWindowFollowing,
  useWorkOrderWindowSync,
} from '@/hooks/useWorkOrderWindow';
import {
  WORK_ORDER_WINDOW_NAME,
  WORK_ORDER_WINDOW_PATH,
  isWorkOrderWindowSupported,
  openWorkOrderWindow,
} from '@/lib/workOrderWindow';

/**
 * Header control for the second work order window (#3653). In an ordinary
 * window it opens or raises the follower window; in the follower window it is
 * the "follow the list" toggle. Mounted for every page so the follower keeps
 * following wherever it is navigated.
 */
export function WorkOrderWindowControl() {
  useWorkOrderWindowSync();
  const { t } = useTranslation('workorders');
  const notify = useNotify();
  const isMobile = useIsMobile();
  const canRead = useAuthStore((s) => s.hasPermission('work_orders.read'));
  const hasWindow = useHasWorkOrderWindow();
  const { isWorkOrderWindow, isFollowing, setFollowing } = useWorkOrderWindowFollowing();

  if (isMobile || !canRead || !isWorkOrderWindowSupported()) return null;

  if (isWorkOrderWindow) {
    const label = isFollowing ? t('secondWindow.following') : t('secondWindow.notFollowing');
    return (
      <Tooltip title={label}>
        <IconButton
          onClick={() => setFollowing(!isFollowing)}
          sx={{ ml: 1, ...(isFollowing ? { color: 'primary.main' } : {}) }}
          aria-label={label}
          aria-pressed={isFollowing}
        >
          {isFollowing ? <LinkIcon /> : <LinkOffIcon />}
        </IconButton>
      </Tooltip>
    );
  }

  const label = hasWindow ? t('secondWindow.show') : t('secondWindow.open');
  return (
    <Tooltip title={label}>
      <IconButton
        onClick={() => {
          if (!hasWindow) {
            void openWorkOrderWindow().then((popup) => {
              if (popup === null) notify.warning(t('secondWindow.blockedTitle'), t('secondWindow.blockedDescription'));
            });
            return;
          }
          // An empty URL raises the named window without navigating it away
          // from its work order; a blank result means it had closed after all.
          const existing = window.open('', WORK_ORDER_WINDOW_NAME);
          if (existing?.location.href === 'about:blank') {
            existing.location.href = WORK_ORDER_WINDOW_PATH;
          }
          existing?.focus();
        }}
        sx={{ ml: 1, ...(hasWindow ? { color: 'primary.main' } : {}) }}
        aria-label={label}
      >
        <OpenInNewIcon />
      </IconButton>
    </Tooltip>
  );
}
